import { DeviceType } from '$lib/constants';
import logger from '$lib/logger';
import { coffedModbusSettings } from '$lib/PersistedState.svelte';
import { modbusDebugEnabled } from '$lib/State.svelte';
import { invoke } from '@tauri-apps/api/core';
import { BaseDevice } from '../base';

/**
 * Coffed SR30 roaster device using Modbus TCP/Ethernet
 *
 * Register addresses (from Artisan configuration):
 * - Register 0: Bean Temperature (BT) - uint16, divisor 2
 * - Register 10: Exhaust Temperature (ET) - uint16, divisor 2
 *
 * Modbus TCP connection settings:
 * - Default Port: 502 (standard Modbus TCP port)
 * - Slave ID: 1
 * - Function Code: 3 (Read Holding Registers)
 *
 * Temperature conversion: temperature = raw_value / 2.0
 * Example: Raw value 325 → 325 / 2 = 162.5°C
 */
export class CoffedModbusDevice extends BaseDevice {
  type: DeviceType = DeviceType.COFFED_MODBUS;

  // Register addresses for Coffed SR30
  private static readonly REGISTERS = {
    // Bean Temperature (BT) - Register 0
    BEAN_TEMP: 0,
    // Exhaust Temperature (ET) - Register 10
    EXHAUST_TEMP: 10
  };

  // Temperature divisor for Coffed devices
  private static readonly TEMP_DIVISOR = 2;

  // Number of registers to read (0-10 inclusive = 11 registers)
  private static readonly REGISTER_COUNT = 11;

  protected async onConnect(): Promise<boolean> {
    try {
      await invoke('connect_modbus', {
        host: coffedModbusSettings.current.host,
        port: this.getModbusPort()
      });
      logger.info(
        `Connected to Coffed Modbus device at ${coffedModbusSettings.current.host}:${this.getModbusPort()}`
      );
      return true;
    } catch (error) {
      logger.error(`Failed to connect to Coffed Modbus device: ${error}`);
      return false;
    }
  }

  protected onDisconnect(): void {
    invoke('disconnect_modbus', {
      host: coffedModbusSettings.current.host,
      port: this.getModbusPort()
    }).catch((error) => logger.warn(`Coffed Modbus disconnect error: ${error}`));
  }

  protected async updateReadings(): Promise<void> {
    try {
      // Read registers 0-10 in one request (covers both BT at 0 and ET at 10)
      const results = await this.readModbusRegisters(
        coffedModbusSettings.current.host,
        CoffedModbusDevice.REGISTERS.BEAN_TEMP,
        CoffedModbusDevice.REGISTER_COUNT
      );

      // Extract raw values
      const rawBeanTemp = results[CoffedModbusDevice.REGISTERS.BEAN_TEMP];
      const rawExhaustTemp = results[CoffedModbusDevice.REGISTERS.EXHAUST_TEMP];

      // Apply divisor to convert to actual temperature (°C)
      const beanTempCelsius = rawBeanTemp / CoffedModbusDevice.TEMP_DIVISOR;
      const exhaustTempCelsius = rawExhaustTemp / CoffedModbusDevice.TEMP_DIVISOR;

      if (modbusDebugEnabled.current === true) {
        logger.debug('DEVICE_COFFED_MODBUS_READINGS', {
          allResults: results,
          rawBeanTemp,
          rawExhaustTemp,
          beanTempCelsius,
          exhaustTempCelsius
        });
      }

      this.localReadings = [
        beanTempCelsius, // TC1: Bean Temperature
        exhaustTempCelsius, // TC2: Exhaust Temperature
        null, // TC3: Not used
        null // TC4: Not used
      ];
    } catch (error) {
      logger.error(`Coffed Modbus error: ${error}`);
      throw error;
    }
  }

  /**
   * Read multiple holding registers from the Modbus device
   *
   * @param host Modbus TCP host address
   * @param startRegister Starting register address
   * @param count Number of registers to read
   * @returns Array of register values
   */
  private async readModbusRegisters(
    host: string,
    startRegister: number,
    count: number
  ): Promise<number[]> {
    try {
      return await invoke('read_modbus', {
        host,
        port: this.getModbusPort(),
        reg: startRegister,
        count
      });
    } catch (error) {
      throw new Error(
        `Failed to read Modbus registers ${startRegister}-${startRegister + count - 1}: ${error}`
      );
    }
  }
}

export default CoffedModbusDevice;
